import { createContext, useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

interface NavigationContextValue {}

const NavigationContext = createContext<NavigationContextValue | undefined>(
  undefined
);

interface PortalMessage {
  pushRoute?: string;
}

const APP_IDENTIFIER = 'catalog';

function removeBasename(route: string, basename: string): string {
  const trimmedRoute = route.trim();

  if (!trimmedRoute) {
    return '/';
  }

  if (basename && basename !== '/') {
    let normalizedBasename = basename;
    if (normalizedBasename.endsWith('/')) {
      normalizedBasename = normalizedBasename.slice(0, -1);
    }
    if (!normalizedBasename.startsWith('/')) {
      normalizedBasename = `/${normalizedBasename}`;
    }

    if (trimmedRoute.startsWith(normalizedBasename)) {
      const pathAfterBasename = trimmedRoute.substring(
        normalizedBasename.length
      );
      return pathAfterBasename || '/';
    }
  }

  return trimmedRoute.startsWith('/') ? trimmedRoute : `/${trimmedRoute}`;
}

function isCurrentAppActive(): boolean {
  const currentPath = window.location.pathname;
  return currentPath.includes(`/app/${APP_IDENTIFIER}`);
}

export function NavigationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const history = useHistory();
  const isMountedRef = useRef(true);
  const basenameRef = useRef<string>('');

  useEffect(() => {
    const base = window.__APP_BASE__ || '/';
    basenameRef.current = base.endsWith('/') ? base.slice(0, -1) : base;
  }, []);

  const isCatalogRoute = useCallback((route: string): boolean => {
    if (!route) return false;

    const catalogPattern = /\/app\/catalog(\/|$)/;
    return catalogPattern.test(route);
  }, []);

  const handleMessage = useCallback(
    (event: Event): void => {
      if (!isMountedRef.current) {
        return;
      }

      if (!isCurrentAppActive()) {
        return;
      }

      const customEvent = event as CustomEvent<PortalMessage>;
      const message = customEvent?.detail;

      if (!message?.pushRoute) {
        return;
      }

      const route = message.pushRoute;
      const normalizedRoute = removeBasename(route, basenameRef.current);

      const isCatalog =
        isCatalogRoute(route) || isCatalogRoute(normalizedRoute);

      if (!isCatalog) {
        window.location.href = route;
        return;
      }

      history.push(normalizedRoute);
    },
    [history, isCatalogRoute]
  );

  useEffect(() => {
    isMountedRef.current = true;

    window.addEventListener('messageToApp', handleMessage);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('messageToApp', handleMessage);
    };
  }, [handleMessage]);

  return (
    <NavigationContext.Provider value={{}}>
      {children}
    </NavigationContext.Provider>
  );
}
